import { useState } from 'react'
import {
  useEmailTemplates,
  useEmailConfig,
  useEmailAudit,
  EmailAuditEntry,
} from '@/hooks/useAdminEmails'
import EmailTemplateEditor from '@/components/admin/EmailTemplateEditor'

type EmailTab = 'templates' | 'provider' | 'audit'

const TABS = [
  { key: 'templates' as const, label: 'Templates', icon: '📝' },
  { key: 'provider' as const, label: 'Provider', icon: '⚡' },
  { key: 'audit' as const, label: 'Audit Log', icon: '📊' },
]

export default function EmailManagement() {
  const [activeTab, setActiveTab] = useState<EmailTab>('templates')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)

  return (
    <div className="min-h-screen bg-[#0a0e14] relative overflow-hidden">
      {/* Subtle scan-line effect */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.02]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, #FFB84D 2px, #FFB84D 4px)'
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Header - Typewriter Style */}
        <div className="mb-8 border-b border-[#FFB84D]/20 pb-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[#FFB84D] text-3xl">📧</span>
            <h1 className="text-3xl font-bold text-[#FFB84D] tracking-wider uppercase" style={{
              fontFamily: '"Courier New", monospace',
              textShadow: '0 0 20px rgba(255, 184, 77, 0.3)',
              letterSpacing: '0.1em'
            }}>
              Email Command Center
            </h1>
          </div>
          <p className="text-[#8b949e] text-sm ml-11" style={{ fontFamily: '"Courier New", monospace' }}>
            CLASSIFIED // Transactional Email Management System
          </p>
        </div>

        {/* Tab Navigation - Terminal Style */}
        <div className="mb-6">
          <div className="flex gap-2">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  relative px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all duration-200
                  ${activeTab === tab.key
                    ? 'bg-[#FFB84D]/10 text-[#FFB84D] border-l-2 border-[#FFB84D] shadow-[0_0_20px_rgba(255,184,77,0.2)]'
                    : 'text-[#8b949e] hover:text-[#FFB84D]/70 hover:bg-[#FFB84D]/5'
                  }
                `}
                style={{
                  clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)'
                }}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {activeTab === 'templates' && (
            <TemplatesTab
              onSelectTemplate={setSelectedTemplate}
            />
          )}
          {activeTab === 'provider' && <ProviderTab />}
          {activeTab === 'audit' && <AuditTab />}
        </div>
      </div>

      {/* Template Editor Drawer */}
      {selectedTemplate && (
        <EmailTemplateEditor
          emailType={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </div>
  )
}

// =============================================================================
// TEMPLATES TAB
// =============================================================================

function TemplatesTab({ onSelectTemplate }: { onSelectTemplate: (type: string) => void }) {
  const { data: templates, isLoading, error } = useEmailTemplates()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#FFB84D] font-mono animate-pulse">
          [LOADING TEMPLATES...]
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded p-4 font-mono text-red-400">
        ERROR: Failed to load templates
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {templates?.map((template) => (
        <button
          key={template.email_type}
          onClick={() => onSelectTemplate(template.email_type)}
          className="w-full bg-[#141c24]/80 border border-[#2a3542] hover:border-[#FFB84D]/50
                     rounded-sm p-4 text-left transition-all duration-200 group
                     hover:shadow-[0_0_20px_rgba(255,184,77,0.1)] hover:translate-x-1"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-mono text-[#8b949e] uppercase tracking-wider">
                  [{template.email_type.replace(/_/g, '-')}]
                </span>
                <StatusBadge
                  isActive={template.is_active}
                  hasCustomBody={template.has_custom_body}
                />
              </div>
              <div className="text-[#e8eaed] font-medium mb-1 group-hover:text-[#FFB84D] transition-colors">
                {template.subject_template}
              </div>
              {template.updated_at && (
                <div className="text-xs text-[#8b949e] font-mono">
                  Modified: {new Date(template.updated_at).toLocaleString()}
                </div>
              )}
            </div>
            <div className="text-[#FFB84D] opacity-0 group-hover:opacity-100 transition-opacity">
              →
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// PROVIDER TAB
// =============================================================================

function ProviderTab() {
  const { data: config, isLoading, error, refetch } = useEmailConfig()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#FFB84D] font-mono animate-pulse">
          [LOADING CONFIGURATION...]
        </div>
      </div>
    )
  }

  if (error || !config) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded p-4 font-mono text-red-400">
        ERROR: Failed to load configuration
      </div>
    )
  }

  return (
    <div className="max-w-3xl">
      {/* Health Status Card */}
      <div className="bg-[#141c24]/80 border border-[#2a3542] rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-mono uppercase tracking-wider text-[#FFB84D]">
            System Status
          </h2>
          <HealthBadge status={config.health} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ConfigItem label="Provider" value={config.provider.toUpperCase()} />
          <ConfigItem label="Health" value={config.health.toUpperCase()} />
          <ConfigItem label="Sender Address" value={config.sender_address} />
          <ConfigItem label="Sender Name" value={config.sender_name} />
          <ConfigItem label="Base URL" value={config.base_url} className="col-span-2" />
        </div>

        <button
          onClick={() => refetch()}
          className="mt-6 px-4 py-2 bg-[#FFB84D]/10 hover:bg-[#FFB84D]/20
                     border border-[#FFB84D]/30 text-[#FFB84D] font-mono text-sm
                     uppercase tracking-wider transition-all duration-200
                     hover:shadow-[0_0_15px_rgba(255,184,77,0.3)]"
        >
          Test Connectivity →
        </button>
      </div>

      {/* Information Panel */}
      <div className="bg-[#1a1f2e]/50 border border-[#FFB84D]/20 rounded-sm p-6">
        <div className="flex items-start gap-3">
          <span className="text-[#FFB84D] text-xl">⚠️</span>
          <div>
            <div className="font-mono text-sm text-[#FFB84D] mb-2">CONFIGURATION NOTE</div>
            <div className="text-[#8b949e] text-sm font-mono leading-relaxed">
              Provider settings are managed via environment variables.
              Contact DevOps to change EMAIL_PROVIDER, BREVO_API_KEY, or sender configuration.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// AUDIT TAB
// =============================================================================

function AuditTab() {
  const [page, setPage] = useState(1)
  const [emailTypeFilter, setEmailTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [recipientSearch, setRecipientSearch] = useState<string>('')
  const [selectedEntry, setSelectedEntry] = useState<EmailAuditEntry | null>(null)

  const { data, isLoading, error } = useEmailAudit({
    page,
    page_size: 20,
    email_type: emailTypeFilter || undefined,
    status: statusFilter || undefined,
    recipient_email: recipientSearch || undefined,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#FFB84D] font-mono animate-pulse">
          [SCANNING AUDIT LOG...]
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded p-4 font-mono text-red-400">
        ERROR: Failed to load audit log
      </div>
    )
  }

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 0

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search recipient..."
          value={recipientSearch}
          onChange={(e) => setRecipientSearch(e.target.value)}
          className="bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                     text-[#e8eaed] text-sm font-mono
                     focus:outline-none focus:border-[#FFB84D]/50"
        />
        <select
          value={emailTypeFilter}
          onChange={(e) => setEmailTypeFilter(e.target.value)}
          className="bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                     text-[#e8eaed] text-sm font-mono
                     focus:outline-none focus:border-[#FFB84D]/50"
        >
          <option value="">All Types</option>
          <option value="mfa_code">MFA Code</option>
          <option value="password_reset">Password Reset</option>
          <option value="welcome">Welcome</option>
          <option value="magic_link">Magic Link</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                     text-[#e8eaed] text-sm font-mono
                     focus:outline-none focus:border-[#FFB84D]/50"
        >
          <option value="">All Status</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="queued">Queued</option>
        </select>
        <div className="flex items-center justify-end gap-2 font-mono text-xs text-[#8b949e]">
          <span>Total: {data?.total || 0}</span>
        </div>
      </div>

      {/* Audit Entries */}
      <div className="space-y-2">
        {data?.items.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setSelectedEntry(entry)}
            className="w-full bg-[#141c24]/80 border border-[#2a3542] hover:border-[#FFB84D]/30
                       rounded-sm p-3 text-left transition-all duration-200 group"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 grid grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-[#8b949e] font-mono mb-1">TYPE</div>
                  <div className="text-sm text-[#e8eaed]">{entry.email_type}</div>
                </div>
                <div>
                  <div className="text-xs text-[#8b949e] font-mono mb-1">RECIPIENT</div>
                  <div className="text-sm text-[#e8eaed] truncate">{entry.recipient_email}</div>
                </div>
                <div>
                  <div className="text-xs text-[#8b949e] font-mono mb-1">STATUS</div>
                  <AuditStatusBadge status={entry.status} />
                </div>
                <div>
                  <div className="text-xs text-[#8b949e] font-mono mb-1">SENT</div>
                  <div className="text-sm text-[#e8eaed]">
                    {entry.sent_at ? new Date(entry.sent_at).toLocaleString() : '-'}
                  </div>
                </div>
              </div>
              <div className="text-[#FFB84D] opacity-0 group-hover:opacity-100 transition-opacity ml-4">
                →
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-[#141c24] border border-[#2a3542] text-[#8b949e]
                       font-mono text-sm disabled:opacity-30 hover:border-[#FFB84D]/50"
          >
            ←
          </button>
          <span className="text-[#8b949e] font-mono text-sm">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 bg-[#141c24] border border-[#2a3542] text-[#8b949e]
                       font-mono text-sm disabled:opacity-30 hover:border-[#FFB84D]/50"
          >
            →
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedEntry && (
        <AuditDetailModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </div>
  )
}

// =============================================================================
// UTILITY COMPONENTS
// =============================================================================

function StatusBadge({ isActive, hasCustomBody }: { isActive: boolean; hasCustomBody: boolean }) {
  if (!hasCustomBody) {
    return (
      <span className="px-2 py-0.5 text-xs font-mono bg-[#2a3542] text-[#8b949e]
                       border border-[#2a3542] rounded-sm">
        [DEFAULT]
      </span>
    )
  }

  return (
    <span className={`
      px-2 py-0.5 text-xs font-mono rounded-sm border
      ${isActive
        ? 'bg-[#FFB84D]/10 text-[#FFB84D] border-[#FFB84D]/30 shadow-[0_0_10px_rgba(255,184,77,0.2)]'
        : 'bg-[#2a3542]/50 text-[#8b949e] border-[#2a3542]'
      }
    `}>
      {isActive ? '[ACTIVE]' : '[CUSTOM]'}
    </span>
  )
}

function HealthBadge({ status }: { status: string }) {
  const colors = {
    healthy: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30', glow: 'shadow-[0_0_15px_rgba(34,197,94,0.3)]' },
    degraded: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30', glow: 'shadow-[0_0_15px_rgba(234,179,8,0.3)]' },
    not_configured: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', glow: 'shadow-[0_0_15px_rgba(239,68,68,0.3)]' },
  }

  const style = colors[status as keyof typeof colors] || colors.not_configured

  return (
    <span className={`
      px-3 py-1 text-xs font-mono uppercase rounded-sm border
      ${style.bg} ${style.text} ${style.border} ${style.glow}
    `}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function AuditStatusBadge({ status }: { status: string }) {
  const colors = {
    sent: 'text-green-400',
    failed: 'text-red-400',
    queued: 'text-yellow-400',
  }

  return (
    <span className={`text-sm font-mono ${colors[status as keyof typeof colors] || 'text-[#8b949e]'}`}>
      {status.toUpperCase()}
    </span>
  )
}

function ConfigItem({ label, value, className = '' }: { label: string; value: string; className?: string }) {
  return (
    <div className={className}>
      <div className="text-xs text-[#8b949e] font-mono mb-1 uppercase tracking-wider">{label}</div>
      <div className="text-[#e8eaed] font-mono text-sm break-all">{value}</div>
    </div>
  )
}

function AuditDetailModal({ entry, onClose }: { entry: EmailAuditEntry; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
         onClick={onClose}>
      <div className="bg-[#141c24] border border-[#FFB84D]/30 rounded-sm max-w-2xl w-full p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#2a3542]">
          <h3 className="text-xl font-mono uppercase tracking-wider text-[#FFB84D]">
            Transmission Details
          </h3>
          <button
            onClick={onClose}
            className="text-[#8b949e] hover:text-[#FFB84D] transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 font-mono text-sm">
          <ConfigItem label="ID" value={entry.id} />
          <ConfigItem label="Type" value={entry.email_type} />
          <ConfigItem label="Recipient" value={entry.recipient_email} />
          <ConfigItem label="Subject" value={entry.subject} />
          <ConfigItem label="Status" value={entry.status.toUpperCase()} />
          <ConfigItem label="Provider" value={entry.provider || 'N/A'} />
          <ConfigItem label="Sent At" value={entry.sent_at ? new Date(entry.sent_at).toLocaleString() : 'N/A'} />
          <ConfigItem label="Created At" value={new Date(entry.created_at).toLocaleString()} />

          {entry.error_message && (
            <div>
              <div className="text-xs text-red-400 font-mono mb-1 uppercase tracking-wider">Error</div>
              <div className="text-red-400 bg-red-900/20 border border-red-500/30 p-3 rounded">
                {entry.error_message}
              </div>
            </div>
          )}

          {entry.tags && entry.tags.length > 0 && (
            <div>
              <div className="text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">Tags</div>
              <div className="flex flex-wrap gap-2">
                {entry.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-[#2a3542] text-[#8b949e] text-xs rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
